import { api } from './api';

const root = (projectId) => `/projects/${projectId}/pdf-splitter`;
const unwrap = async (promise) => (await promise).data;

export const pdfSplitterAPI = {
  create: (p, payload) => unwrap(api.post(`${root(p)}/jobs`, payload)),
  list: (p) => unwrap(api.get(`${root(p)}/jobs`)),
  get: (p, id) => unwrap(api.get(`${root(p)}/jobs/${id}`)),
  parts: (p, id, partNumbers) => unwrap(api.post(`${root(p)}/jobs/${id}/parts`, { part_numbers: partNumbers })),
  complete: (p, id, parts) => unwrap(api.post(`${root(p)}/jobs/${id}/complete-multipart`, { parts })),
  uploaded: (p, id) => unwrap(api.post(`${root(p)}/jobs/${id}/uploaded`)),
  abort: (p, id) => unwrap(api.post(`${root(p)}/jobs/${id}/abort`)),
  savePlan: (p, id, revision, documents) => unwrap(api.put(`${root(p)}/jobs/${id}/plan`, { revision, documents })),
  generate: (p, id, revision, documents) => unwrap(api.post(`${root(p)}/jobs/${id}/generate`, { revision, documents })),
  sourceUrl: (p, id) => unwrap(api.get(`${root(p)}/jobs/${id}/source-url`)),
  outputUrl: (p, id, outputId, download = false) => unwrap(api.get(`${root(p)}/jobs/${id}/outputs/${outputId}/url`, { params: { download } })),
  zipUrl: (p, id) => unwrap(api.get(`${root(p)}/jobs/${id}/zip-url`)),
  cancel: (p, id) => unwrap(api.post(`${root(p)}/jobs/${id}/cancel`)),
};

const putWithProgress = (url, blob, headers, onProgress) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', url, true);
  Object.entries(headers || {}).forEach(([key, value]) => xhr.setRequestHeader(key, value));
  xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress?.(event.loaded); };
  xhr.onload = () => xhr.status >= 200 && xhr.status < 300
    ? resolve(xhr.getResponseHeader('ETag'))
    : reject(new Error(`S3 rejected upload (HTTP ${xhr.status})`));
  xhr.onerror = () => reject(new Error('Direct S3 upload failed. Check network and bucket CORS settings.'));
  xhr.onabort = () => reject(new Error('Upload cancelled'));
  xhr.send(blob);
});

const retryPut = async (url, blob, headers, onProgress, maxRetries = 3) => {
  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    try { return await putWithProgress(url, blob, headers, onProgress); }
    catch (error) { lastError = error; if (attempt + 1 < maxRetries) await new Promise((r) => setTimeout(r, 500 * 2 ** attempt)); }
  }
  throw lastError;
};

/** Direct browser -> S3, never proxies bytes through FastAPI. */
export async function uploadSplitterPDF(projectId, file, onProgress) {
  if (!file.name.toLowerCase().endsWith('.pdf') || !file.size) throw new Error('Select a nonempty PDF file.');
  const requestId = crypto.randomUUID();
  const created = await pdfSplitterAPI.create(projectId, {
    filename: file.name, file_size: file.size, client_request_id: requestId,
  });
  const jobId = created.job.id;
  if (created.job.status !== 'pending_upload') return created.job;
  const auth = created.upload;
  if (!auth) throw new Error('Upload session cannot be resumed; create a new split job.');
  const uploaded = new Map();
  const inFlight = new Map();
  const report = () => {
    const uploadedBytes = [...uploaded.values()].reduce((a, b) => a + b, 0);
    const activeBytes = [...inFlight.values()].reduce((a, b) => a + b, 0);
    onProgress?.(Math.min(99, Math.round(100 * (uploadedBytes + activeBytes) / file.size)));
  };
  try {
    if (auth.mode === 'put') {
      await retryPut(auth.url, file, auth.headers, (loaded) => { inFlight.set(1, loaded); report(); });
    } else if (auth.mode === 'multipart') {
      const allParts = [];
      const concurrency = 4;
      for (let batchStart = 1; batchStart <= auth.part_count; batchStart += 16) {
        const nums = Array.from({ length: Math.min(16, auth.part_count - batchStart + 1) }, (_, i) => batchStart + i);
        const urls = await pdfSplitterAPI.parts(projectId, jobId, nums);
        const urlByPart = new Map(urls.parts.map((part) => [part.part_number, part.url]));
        let cursor = 0;
        const uploadNext = async () => {
          while (cursor < nums.length) {
            const number = nums[cursor++];
            const start = (number - 1) * auth.part_size;
            const end = Math.min(file.size, start + auth.part_size);
            const blob = file.slice(start, end);
            const etag = await retryPut(urlByPart.get(number), blob, {}, (loaded) => {
              inFlight.set(number, loaded); report();
            });
            if (!etag) throw new Error('S3 did not expose the ETag header. Configure S3 CORS ExposeHeaders: ETag.');
            inFlight.delete(number);
            uploaded.set(number, blob.size);
            allParts.push({ part_number: number, etag });
            report();
          }
        };
        await Promise.all(Array.from({ length: Math.min(concurrency, nums.length) }, () => uploadNext()));
      }
      await pdfSplitterAPI.complete(projectId, jobId, allParts);
    } else {
      throw new Error('Unknown S3 upload mode');
    }
    const result = await pdfSplitterAPI.uploaded(projectId, jobId);
    onProgress?.(100);
    return result;
  } catch (error) {
    try { await pdfSplitterAPI.abort(projectId, jobId); } catch (_) { /* backend cleanup worker handles abandoned multipart sessions */ }
    throw error;
  }
}
